import { Maybe } from '@varo/graphql-schema-types';
import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import Illustration, {
  IllustrationKeyType,
} from 'shared/components/vxl/Illustration/Illustration';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import theme from 'shared/styles/theme';

export interface ProductOfferCardNonActionableProps {
  /** Indicates main title of the card  */
  titleText: string;
  /** Body text on the card */
  bodyText?: string;
  /** Grey colored action text that to be shown along with chevron*/
  actionText?: Maybe<string>;
  /** illustrationKey of the image to be displayed*/
  illustrationKey: IllustrationKeyType;
  /** Background color of the image container */
  imageBackgroundColor: string;
}

const titleTextLineLimit = 2;
const bodyTextLineLimit = 3;
const actionTextLineLimit = 1;

export const ProductOfferCardNonActionable = ({
  titleText,
  bodyText,
  actionText,
  illustrationKey,
  imageBackgroundColor,
}: ProductOfferCardNonActionableProps) => {
  return (
    <View style={baseStyles.container}>
      <View style={baseStyles.textContainer}>
        <TitleSmText
          style={baseStyles.titleText}
          numberOfLines={titleTextLineLimit}
        >
          {titleText}
        </TitleSmText>
        {bodyText ? (
          <BodySmText
            style={baseStyles.bodyText}
            numberOfLines={bodyTextLineLimit}
          >
            {bodyText}
          </BodySmText>
        ) : null}
        {actionText ? (
          <View style={baseStyles.actionContainer}>
            <BodyXSText
              style={[baseStyles.actionText, baseStyles.actionText]}
              numberOfLines={actionTextLineLimit}
            >
              {actionText}
            </BodyXSText>
            <View style={baseStyles.chevronContainer}>
              <Icon
                iconKey="CHEVRON_RIGHT"
                iconSize="XS"
                iconColor={theme.color.text.secondary}
              />
            </View>
          </View>
        ) : null}
      </View>
      <View style={baseStyles.imageContainer}>
        <View
          style={[
            baseStyles.imageBackground,
            { backgroundColor: imageBackgroundColor },
          ]}
        />
        <View style={baseStyles.image}>
          <Illustration illustration={illustrationKey} />
        </View>
      </View>
    </View>
  );
};

const baseStyles = StyleSheet.create({
  actionContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingRight: theme.spacing.s,
  },
  actionText: {
    color: theme.color.text.secondary,
    flexShrink: 1,
  },
  bodyText: {
    color: theme.color.text.primary,
  },
  chevronContainer: {
    flexBasis: 1,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    borderColor: theme.color.background.primary,
    borderRadius: 4,
    borderWidth: 1,
    display: 'flex',
    flexDirection: 'row',
    minHeight: 120,
    width: '100%',
    ...Platform.select({
      web: {
        alignItems: 'stretch',
      },
      default: {
        alignItems: 'center',
      },
    }),
  },
  image: {
    bottom: 0,
    height: 82,
    marginBottom: theme.spacing.s,
    position: 'absolute',
    width: 85,
  },
  imageBackground: {
    alignSelf: 'flex-end',
    borderBottomRightRadius: 4,
    borderTopRightRadius: 4,
    flexGrow: 1,
    marginVertical: theme.spacing.s,
    minHeight: 82,
    width: 58,
    ...Platform.select({
      web: {
        alignItems: 'stretch',
        height: 'auto',
      },
    }),
  },
  imageContainer: {
    alignItems: 'flex-end',
    flexGrow: 1,
    marginRight: theme.spacing.s,
    width: 93,
  },
  textContainer: {
    flexShrink: 1,
    gap: theme.spacing.xs,
    justifyContent: 'center',
    paddingLeft: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  titleText: {
    color: theme.color.text.primary,
  },
});

export default ProductOfferCardNonActionable;
